import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Navbar from "../../components/Navbar";
import PropertyLocationMap from "../../components/PropertyLocationMap";
import { roomsAPI } from "../../services/meetingsAPI";
import { getImageUrl } from "../../utils/imageHelper";
import {
  FaMapMarkerAlt,
  FaInfoCircle,
  FaCheck,
  FaWifi,
  FaParking,
  FaBolt,
  FaVideo,
  FaSnowflake,
  FaUsers,
  FaCoffee,
  FaChalkboardTeacher,
  FaDesktop,
  FaVolumeUp,
  FaBuilding,
  FaStar,
  FaShieldAlt,
  FaQuestionCircle,
  FaSpinner,
  FaCalendarAlt,
} from "react-icons/fa";
import "./MeetingRoomDetailsPage.css";
import "../PropertyDetailsPage.css";
import "../PropertyDetailsPage-map.css";
import "../PropertyDetailsPage-qa.css";

function MeetingRoomAmenityIcon({ label }) {
  const n = String(label || "").toLowerCase();
  if (n.includes("wifi") || n.includes("wi-fi")) return <FaWifi aria-hidden />;
  if (n.includes("park")) return <FaParking aria-hidden />;
  if (n.includes("power") || n.includes("backup")) return <FaBolt aria-hidden />;
  if (n.includes("video") || n.includes("conferenc") || n.includes("camera")) return <FaVideo aria-hidden />;
  if (n.includes("projector") || n.includes("screen") || n.includes("display")) return <FaDesktop aria-hidden />;
  if (n.includes("ac") || n.includes("air") || n.includes("climate")) return <FaSnowflake aria-hidden />;
  if (n.includes("whiteboard") || n.includes("board")) return <FaChalkboardTeacher aria-hidden />;
  if (n.includes("coffee") || n.includes("tea")) return <FaCoffee aria-hidden />;
  if (n.includes("sound") || n.includes("speaker") || n.includes("mic")) return <FaVolumeUp aria-hidden />;
  if (n.includes("reception") || n.includes("front desk")) return <FaBuilding aria-hidden />;
  if (n.includes("natural") || n.includes("light") || n.includes("daylight") || n.includes("window")) return <FaStar aria-hidden />;
  if (n.includes("secur") || n.includes("cctv")) return <FaShieldAlt aria-hidden />;
  if (n.includes("people") || n.includes("seat") || n.includes("team")) return <FaUsers aria-hidden />;
  return <FaCheck aria-hidden />;
}

const MeetingRoomDetailsPage = () => {
  const { roomId } = useParams();
  const navigate = useNavigate();
  const [room, setRoom] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("description");
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [showFullscreen, setShowFullscreen] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [roomQA, setRoomQA] = useState([]);
  const [loadingQA, setLoadingQA] = useState(false);
  const [question, setQuestion] = useState("");
  const [questionSubmitting, setQuestionSubmitting] = useState(false);
  const [questionSubmitted, setQuestionSubmitted] = useState(false);
  const [showAllQuestions, setShowAllQuestions] = useState(false);

  useEffect(() => {
    try {
      const storedUser = localStorage.getItem("user");
      if (storedUser) {
        setCurrentUser(JSON.parse(storedUser));
      }
    } catch {
      setCurrentUser(null);
    }
  }, []);

  useEffect(() => {
    const fetchRoom = async () => {
      if (!roomId) {
        setError("Room not found");
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        let data = null;
        try {
          const res = await roomsAPI.getRoom(roomId);
          data = res.data;
        } catch (_) {}

        const localRooms = JSON.parse(localStorage.getItem("meetingRooms") || "[]");
        const local = localRooms.find(
          (r) => String(r.id || r._id || "") === String(roomId)
        );

        const toResolvedUrls = (rawList) =>
          (rawList || [])
            .filter(Boolean)
            .map((img) =>
              typeof img === "string" && (img.startsWith("http") || img.startsWith("data:"))
                ? img
                : getImageUrl(img)
            );

        // Match MeetingSearchResults: API room wins in list merge, so first hero image must match search cards.
        const apiUrls = toResolvedUrls(data?.images);
        const localUrls = toResolvedUrls(local?.images);
        const seen = new Set();
        const mergedImageUrls = [];
        for (const u of [...apiUrls, ...localUrls]) {
          if (u && !seen.has(u)) {
            seen.add(u);
            mergedImageUrls.push(u);
          }
        }
        const singleFallback =
          local?.image && typeof local.image === "string"
            ? local.image.startsWith("http") || local.image.startsWith("data:")
              ? local.image
              : getImageUrl(local.image)
            : data?.image && typeof data.image === "string"
              ? data.image.startsWith("http") || data.image.startsWith("data:")
                ? data.image
                : getImageUrl(data.image)
              : null;
        const finalImages =
          mergedImageUrls.length > 0
            ? mergedImageUrls
            : singleFallback
            ? [singleFallback]
            : [];

        const merged = {
          id: roomId,
          _id: roomId,
          roomName: data?.name || local?.roomName || local?.name || "Meeting Room",
          description:
            data?.description || local?.description || "A professional meeting space equipped for teams. Book for your next meeting or workshop.",
          services: local?.services ?? data?.services ?? "",
          surroundings: local?.surroundings ?? data?.surroundings ?? "",
          comments: local?.comments ?? data?.comments ?? "",
          rules: local?.rules ?? data?.rules ?? "",
          policy: local?.policy ?? data?.policy ?? "",
          city: data?.location?.city || data?.city || local?.city || "—",
          address: local?.address || data?.address || local?.city || "",
          state: data?.state || local?.state || "",
          pincode: data?.pincode || local?.pincode || "",
          capacity: data?.capacity ?? local?.capacity ?? 0,
          amenities: data?.amenities?.length ? data.amenities : local?.amenities || [],
          pricePerHour: data?.pricePerHour ?? local?.pricePerHour ?? 0,
          userId: data?.userId || local?.userId || null,
          cleaningResponsibility: data?.cleaningResponsibility || local?.cleaningResponsibility || "member_clean",
          cleaningCharge: data?.cleaningCharge ?? local?.cleaningCharge ?? 0,
          images: finalImages,
          rating: data?.rating ?? local?.rating ?? 4.5,
          reviews: data?.reviews ?? local?.reviews ?? 0,
          ownerName:
            (data?.userId && typeof data.userId === "object" ? data.userId.name : "") ||
            data?.hostName ||
            local?.hostName ||
            local?.ownerName ||
            "Owner",
          ownerEmail:
            (data?.userId && typeof data.userId === "object" ? data.userId.email : "") ||
            data?.hostEmail ||
            local?.hostEmail ||
            local?.ownerEmail ||
            "",
        };

        setRoom(merged);
      } catch (e) {
        setError("Could not load room details.");
        setRoom(null);
      } finally {
        setLoading(false);
      }
    };

    fetchRoom();
  }, [roomId]);

  useEffect(() => {
    const fetchRoomQA = async () => {
      if (!roomId) return;
      setLoadingQA(true);
      try {
        const res = await roomsAPI.getRoomQA(roomId);
        const questions = res?.data?.questions;
        setRoomQA(Array.isArray(questions) ? questions : []);
      } catch (err) {
        console.error("Error fetching meeting room Q&A:", err);
        setRoomQA([]);
      } finally {
        setLoadingQA(false);
      }
    };

    fetchRoomQA();
  }, [roomId]);

  if (loading) {
    return (
      <div className="meeting-room-details-page property-details-page">
        <Navbar isScrolled={false} />
        <div className="property-details-loading">
          <div className="spinner" />
          <p>Loading room details…</p>
        </div>
      </div>
    );
  }

  if (error || !room) {
    return (
      <div className="meeting-room-details-page property-details-page">
        <Navbar isScrolled={false} />
        <div className="property-not-found">
          <p>{error || "Room not found."}</p>
          <button type="button" className="mrd-btn-back" onClick={() => navigate("/meeting-home")}>
            Back to Meeting Home
          </button>
        </div>
      </div>
    );
  }

  const propertyImages =
    room.images?.length > 0
      ? room.images
      : ["https://images.unsplash.com/photo-1497366811353-6870744d04b2?w=1200&q=80"];
  const addressDisplay = room.address || room.city || "—";
  const cleaningResponsibilityLabel = room.cleaningResponsibility === "owner_clean"
    ? "Owner cleans the property"
    : "Members clean the property";
  const cleaningChargeValue = room.cleaningResponsibility === "owner_clean"
    ? Math.max(0, Number(room.cleaningCharge) || 0)
    : 0;

  const handleImageClick = (index) => {
    setSelectedImageIndex(index);
    setShowFullscreen(true);
  };

  const handlePrevImage = () => {
    setSelectedImageIndex((i) => (i <= 0 ? propertyImages.length - 1 : i - 1));
  };
  const handleNextImage = () => {
    setSelectedImageIndex((i) => (i >= propertyImages.length - 1 ? 0 : i + 1));
  };

  const qaOwnerInfo = {
    name: room.ownerName || room.hostName || room.userId?.name || "Owner",
    email: room.ownerEmail || room.hostEmail || room.userId?.email || "Not available",
  };

  const handleSubmitQuestion = async () => {
    if (!currentUser) {
      toast.warning("Please sign in to ask a question");
      return;
    }
    if (!question.trim() || !roomId || questionSubmitting) return;

    setQuestionSubmitting(true);
    try {
      const res = await roomsAPI.submitRoomQuestion(roomId, {
        question: question.trim(),
      });
      if (res?.data?.success) {
        setQuestionSubmitted(true);
        setQuestion("");
      } else {
        toast.error(res?.data?.message || "Failed to submit question. Please try again.");
      }
    } catch (err) {
      console.error("Submit meeting room question error:", err);
      const message = err?.response?.data?.message || "Failed to submit question. Please try again.";
      toast.error(message);
    } finally {
      setQuestionSubmitting(false);
    }
  };

  const ratingValue = Math.min(5, Math.max(0, Number(room.rating) || 4.5));
  const reviewsCount = Math.max(0, Math.floor(Number(room.reviews) || 0));
  const heroImageSrc = propertyImages[0];

  return (
    <div className="meeting-room-details-page property-details-page">
      <Navbar isScrolled={false} />

      {/* Full-width hero — room image, dark overlay, centered title + rating (matches design reference) */}
      <section className="mrd-hero-banner" aria-label="Meeting room">
        <div className="mrd-hero-banner-media">
          <img
            src={heroImageSrc}
            alt=""
            className="mrd-hero-banner-img"
            loading="eager"
          />
        </div>
        <div className="mrd-hero-banner-scrim" aria-hidden />
        <button
          type="button"
          className="mrd-hero-back"
          onClick={() => navigate(-1)}
        >
          ← Back
        </button>
        <div className="mrd-hero-banner-content">
          <h1 className="mrd-hero-banner-title">{room.roomName}</h1>
          <div className="mrd-hero-rating-pill">
            <span className="mrd-hero-stars" aria-hidden>
              {[1, 2, 3, 4, 5].map((star) => (
                <span
                  key={star}
                  className={star <= Math.round(ratingValue) ? "mrd-hero-star mrd-hero-star--on" : "mrd-hero-star"}
                >
                  ★
                </span>
              ))}
            </span>
            <span className="mrd-hero-rating-text">
              {ratingValue.toFixed(1)} ({reviewsCount} {reviewsCount === 1 ? "Review" : "Reviews"})
            </span>
          </div>
        </div>
        <button
          type="button"
          className="mrd-hero-open-photos"
          onClick={() => handleImageClick(selectedImageIndex)}
        >
          {propertyImages.length > 1 ? "Photos" : "View photo"}
        </button>
      </section>

      <div className="property-details-content mrd-details-below-hero">
        {/* Breadcrumb */}
        <nav className="breadcrumb-nav">
          <button type="button" className="breadcrumb-link" onClick={() => navigate("/meeting-home")}>
            MEETING HOME
          </button>
          <span className="breadcrumb-separator">&gt;</span>
          <button type="button" className="breadcrumb-link" onClick={() => navigate("/meetings/search-results")}>
            SEARCH RESULTS
          </button>
          <span className="breadcrumb-separator">&gt;</span>
          <span className="breadcrumb-current">{room.roomName}</span>
        </nav>

        <p className="mrd-hero-address-line">
          <span aria-hidden>📍</span> {addressDisplay}
        </p>

        {/* Tabs: Description, Services, Surroundings, Comments, Rules, Policy, Schedule */}
        <div className="property-tabs-wrapper mrd-tabs-wrap">
          <div className="property-tabs mrd-tabs">
            {["description", "services", "surroundings", "comments", "rules", "policy"].map(
              (tab) => (
                <button
                  key={tab}
                  type="button"
                  className={`property-tab ${activeTab === tab ? "active" : ""}`}
                  onClick={() => setActiveTab(tab)}
                >
                  {tab.charAt(0).toUpperCase() + tab.slice(1)}
                </button>
              )
            )}
            <button
              key="schedule"
              type="button"
              className="property-tab mrd-schedule-tab"
              onClick={() =>
                navigate(
                  `/meetings/schedule?roomId=${encodeURIComponent(room.id || room._id)}`
                )
              }
              title="View this room's schedule"
            >
              <FaCalendarAlt aria-hidden style={{ marginRight: 6 }} />
              Schedule
            </button>
          </div>
        </div>

        <div className="property-main-layout mrd-main-two-pane">
          {/* Left column: tab content */}
          <div className="property-left-column">
            {activeTab === "description" && (
              <section className="property-section mrd-content-section">
                <h2 className="mrd-card-title">Description</h2>
                <div className="property-description-detailed">
                  <p className="property-description-main mrd-description-lead">{room.description || "—"}</p>
                  {(room.city && room.city !== "—") || room.capacity > 0 || (room.amenities?.length ?? 0) > 0 ? (
                    <div className="mrd-highlights-panel">
                      <h3 className="mrd-highlights-heading">Highlights</h3>
                      <div className="mrd-highlights-row">
                        {room.city && room.city !== "—" && (
                          <div className="mrd-stat-card">
                            <span className="mrd-stat-icon" aria-hidden>
                              <FaMapMarkerAlt />
                            </span>
                            <span className="mrd-stat-label">Location</span>
                            <span className="mrd-stat-value">{room.city}</span>
                          </div>
                        )}
                        {room.capacity > 0 && (
                          <div className="mrd-stat-card">
                            <span className="mrd-stat-icon" aria-hidden>
                              <FaUsers />
                            </span>
                            <span className="mrd-stat-label">Capacity</span>
                            <span className="mrd-stat-value">Up to {room.capacity} people</span>
                          </div>
                        )}
                        {(room.amenities?.length ?? 0) > 0 && (
                          <div className="mrd-stat-card">
                            <span className="mrd-stat-icon" aria-hidden>
                              <FaCheck />
                            </span>
                            <span className="mrd-stat-label">Amenities</span>
                            <span className="mrd-stat-value">{room.amenities.length} listed</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : null}
                </div>
              </section>
            )}

            {activeTab === "services" && (
              <section className="property-section mrd-content-section">
                <h2 className="mrd-card-title">Services</h2>
                <p className="property-description-main mrd-description-lead">{room.services || "—"}</p>
              </section>
            )}

            {activeTab === "surroundings" && (
              <section className="property-section mrd-content-section">
                <h2 className="mrd-card-title">Surroundings</h2>
                <p className="property-description-main mrd-description-lead">{room.surroundings || "—"}</p>
              </section>
            )}

            {activeTab === "comments" && (
              <section className="property-section mrd-content-section">
                <h2 className="mrd-card-title">Comments</h2>
                <p className="property-description-main mrd-description-lead">{room.comments || "—"}</p>
              </section>
            )}

            {activeTab === "rules" && (
              <section className="property-section mrd-content-section">
                <h2 className="mrd-card-title">Rules</h2>
                <p className="property-description-main mrd-description-lead">{room.rules || "—"}</p>
              </section>
            )}

            {activeTab === "policy" && (
              <section className="property-section mrd-content-section">
                <h2 className="mrd-card-title">Policy</h2>
                <p className="property-description-main mrd-description-lead">{room.policy || "—"}</p>
              </section>
            )}

            {activeTab === "description" && (
              <div className="mrd-features-qa-layout">
                <section className="property-section mrd-content-section">
                  <h2 className="mrd-card-title">Features &amp; Amenities</h2>
                  {room.amenities?.length > 0 ? (
                    <div className="mrd-amenities-grid">
                      {room.amenities.map((item, i) => (
                        <div key={i} className="mrd-amenity-tile">
                          <span className="mrd-amenity-icon-wrap">
                            <MeetingRoomAmenityIcon label={item} />
                          </span>
                          <span className="mrd-amenity-label">{item}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="property-description-main mrd-description-lead">No amenities added yet.</p>
                  )}
                </section>

                <section className="property-section qa-section mrd-qa-section">
                  <h2>Questions &amp; Answers</h2>
                  <div className="qa-posted-by">
                    <span className="qa-posted-by-label">Posted by:</span>
                    <span className="qa-posted-by-name">{qaOwnerInfo.name}</span>
                    <span className="qa-posted-by-email">Email: {qaOwnerInfo.email}</span>
                  </div>

                  <div className="qa-container">
                    <div className="qa-left">
                      {loadingQA ? (
                        <div className="qa-loading">
                          <FaSpinner className="spinner" /> Loading Q&amp;A...
                        </div>
                      ) : roomQA.length === 0 ? (
                        <p className="qa-empty">No questions answered yet. Be the first to ask!</p>
                      ) : (
                        <>
                          <div className="qa-list">
                            {(showAllQuestions ? roomQA : roomQA.slice(0, 2)).map((qa) => (
                              <div key={qa._id} className="qa-item">
                                <div className="qa-question">
                                  <FaQuestionCircle />
                                  <strong>{qa.question}</strong>
                                </div>
                                <div className="qa-answer">{qa.answer}</div>
                              </div>
                            ))}
                          </div>
                          {roomQA.length > 2 && (
                            <button
                              type="button"
                              className="btn-toggle-qa"
                              onClick={() => setShowAllQuestions((prev) => !prev)}
                            >
                              {showAllQuestions ? "View less" : "View more"}
                            </button>
                          )}
                        </>
                      )}
                    </div>

                    <div className="qa-right">
                      <div className="ask-question-box">
                        <h3 className="ask-question-title">Ask a question about this room</h3>
                        <p className="ask-question-subtitle">
                          Get quick answers from room staff and past guests
                        </p>
                        {!questionSubmitted ? (
                          <div className="ask-question-form">
                            <textarea
                              className="question-textarea"
                              placeholder="What would you like to know about this room?"
                              value={question}
                              onChange={(e) => {
                                if (e.target.value.length <= 500) setQuestion(e.target.value);
                              }}
                              maxLength={500}
                              rows={4}
                            />
                            <div className="question-meta">
                              <span className="character-count">{question.length}/500 characters</span>
                              {!currentUser && (
                                <span className="login-note">Please sign in to ask questions</span>
                              )}
                            </div>
                            <button
                              type="button"
                              className="btn-submit-question"
                              onClick={handleSubmitQuestion}
                              disabled={!question.trim() || !currentUser || questionSubmitting}
                            >
                              {questionSubmitting ? (
                                <>
                                  <FaSpinner className="spinner" /> Sending...
                                </>
                              ) : (
                                "Submit Question"
                              )}
                            </button>
                          </div>
                        ) : (
                          <div className="question-success">
                            <div className="success-icon"><FaCheck /></div>
                            <h3>Your question has been sent to the owner</h3>
                            <p>
                              The room owner will be notified and will typically respond soon.
                              You&apos;ll receive an update when they reply.
                            </p>
                            <button
                              type="button"
                              className="btn-ask-another"
                              onClick={() => setQuestionSubmitted(false)}
                            >
                              Ask Another Question
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </section>
              </div>
            )}
          </div>

          {/* Right: booking card (left) + map (right), same row on wide screens */}
          <div className="property-right-column mrd-sidebar-split-root">
            <div className="mrd-booking-map-split">
              <div className="mrd-booking-col">
                <div className="booking-summary-card sticky mrd-booking-card">
                  <div className="summary-price">
                    <span className="price-per-night">₹{(room.pricePerHour ?? 0).toLocaleString()}</span>
                    <span className="per-night-label">per hour</span>
                  </div>

                  <div className="summary-tax-note">
                    <FaInfoCircle />
                    <span>+ ₹{Math.round((room.pricePerHour || 0) * 0.18).toLocaleString()} taxes and charges per hour</span>
                  </div>

                  <div className="summary-cancellation-policy">
                    <div className="cancellation-policy-header">
                      <FaCheck />
                      <strong>Free cancellation</strong>
                    </div>
                    <p className="cancellation-policy-detail">
                      Cancel before check-in for a full refund. No cancellation fees.
                    </p>
                  </div>

                  <div className="cleaning-policy-card">
                    <div className="cleaning-policy-head">
                      <div>
                        <p className="cleaning-policy-label">Cleaning Responsibility</p>
                        <p className="cleaning-policy-value">{cleaningResponsibilityLabel}</p>
                      </div>
                      <span className="cleaning-policy-pill">{cleaningChargeValue > 0 ? `₹${cleaningChargeValue.toLocaleString()}` : "₹0"}</span>
                    </div>
                    <p className="cleaning-policy-note">
                      {room.cleaningResponsibility === "owner_clean"
                        ? "A one-time cleaning fee is added to every booking."
                        : "Members tidy up after each meeting, so no cleaning fee is charged."}
                    </p>
                  </div>

                  <button
                    type="button"
                    className="btn-check-availability"
                    onClick={() => navigate(`/meetings/book-flow?roomId=${encodeURIComponent(room.id || room._id)}`)}
                  >
                    Book Now
                  </button>
                </div>
              </div>

              <div className="mrd-map-col">
                <div className="property-map-card mrd-location-card">
                  <h3 className="map-title mrd-location-title">Location</h3>
                  <PropertyLocationMap
                    property={{
                      name: room.roomName,
                      address: room.address,
                      city: room.city,
                      state: room.state,
                      pincode: room.pincode,
                    }}
                    height={320}
                    showPopup={true}
                  />
                  <div className="map-address">
                    <FaMapMarkerAlt />
                    <span>
                      {room.address || room.city || "—"}
                      {room.city && room.state ? `, ${room.state}` : ""}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Fullscreen photo viewer */}
        {showFullscreen && propertyImages.length > 0 && (
          <div
            className="fullscreen-viewer"
            onClick={() => setShowFullscreen(false)}
            role="dialog"
            aria-label="View all photos"
          >
            <button
              type="button"
              className="close-fullscreen"
              onClick={(e) => {
                e.stopPropagation();
                setShowFullscreen(false);
              }}
              aria-label="Close"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
            {propertyImages.length > 1 && (
              <button
                type="button"
                className="nav-image prev"
                onClick={(e) => {
                  e.stopPropagation();
                  handlePrevImage();
                }}
                aria-label="Previous photo"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M15 18l-6-6 6-6" />
                </svg>
              </button>
            )}
            <div className="fullscreen-image-container" onClick={(e) => e.stopPropagation()}>
              <img
                src={propertyImages[selectedImageIndex]}
                alt={`${room.roomName} photo ${selectedImageIndex + 1}`}
                className="mrd-fullscreen-img"
              />
              {propertyImages.length > 1 && (
                <div className="image-counter">
                  {selectedImageIndex + 1} / {propertyImages.length}
                </div>
              )}
            </div>
            {propertyImages.length > 1 && (
              <button
                type="button"
                className="nav-image next"
                onClick={(e) => {
                  e.stopPropagation();
                  handleNextImage();
                }}
                aria-label="Next photo"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 18l6-6-6-6" />
                </svg>
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MeetingRoomDetailsPage;
